/* import __COLOCATED_TEMPLATE__ from './assign-permissions.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { type PulseAccordion } from 'glint/pulse';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type IntlService from 'embercom/services/intl';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { isEmpty } from '@ember/utils';
import { type SalesforceSetupData } from 'embercom/objects/standalone/salesforce';

interface Signature {
  Args: {
    accordion: PulseAccordion;
    onContinue: () => void;
  };
}

export default class AssignPermissionsComponent extends Component<Signature> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.finStandaloneService.salesforceSetupData;
  }

  get isDisabled() {
    return !this.salesforceSetupData.hasPermissionSet;
  }

  get isComplete() {
    return this.salesforceSetupData.hasPermissionSetAssignedToInstaller;
  }

  get isLoading() {
    return this.finStandaloneService.isLoadingSalesforceSetupData;
  }

  get salesforceUserInstallerName() {
    if (this.isLoading || isEmpty(this.salesforceConfig.userId)) {
      return undefined;
    }

    let user = this.finStandaloneService.salesforceSetupData.admins.find(
      (admin) => admin.id === this.salesforceConfig.userId,
    );

    if (user && user.name) {
      return user.name;
    }

    return this.salesforceConfig.userId;
  }

  get installationSteps() {
    return [
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.integrate.assign-permissions-section.step-1',
        ),
      },
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.integrate.assign-permissions-section.step-2',
          {
            finUserName: this.salesforceConfig.finSalesforceUserId,
            htmlSafe: true,
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.integrate.assign-permissions-section.step-3',
          {
            htmlSafe: true,
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.integrate.assign-permissions-section.step-4',
          {
            htmlSafe: true,
          },
        ),
      },
    ];
  }

  @action
  openUrl() {
    safeWindowOpen(
      `${this.salesforceConfig.lightningForceUrl}/lightning/setup/PermSets/page?address=/udd/PermissionSet/assignPermissionSet.apexp?userId=${this.salesforceConfig.userId}`,
      '_blank',
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Integrate::AssignPermissions': typeof AssignPermissionsComponent;
  }
}
